import { useReceptionLocale as useOperationsLocale } from '../../reception/locale';
import CrmNavigationSidebar from '../crm/CrmNavigationSidebar';
import { useReceptionLocale } from '../../reception/locale';
import React, { useEffect, useMemo, useState } from 'react';
import { hasReceptionAllBranchesGrant } from '../../auth/receptionPermissions';
import { useLocation } from 'react-router-dom';
import {
  Award,
  ArrowLeft,
  Bot,
  CalendarDays,
  ChevronDown,
  ChevronRight,
  DollarSign,
  FileText,
  FolderOpen,
  GraduationCap,
  Inbox,
  Languages,
  LayoutDashboard,
  ListChecks,
  Receipt,
  RefreshCcw,
  ShieldCheck,
  UserCheck,
  UserPlus,
  Users,
  WalletCards,
  type LucideIcon,
} from 'lucide-react';

import { PrefetchLink } from '../PrefetchLink';
import { useAuth } from '../../auth/useAuth';
import { useUserProfile } from './useUserProfile';
import { useDashboardCounts } from './useDashboardCounts';
import { type Counts } from './types';
import { getReceptionWorkflowGroup, type ReceptionWorkflowGroup } from '../../pages/reception/receptionWorkflowGroups';

const SECTION_STATE_KEY = 'reception-sidebar-sections';

interface ReceptionSidebarProps {
  counts?: Counts;
  extra?: React.ReactNode;
  collapsed?: boolean;
  onToggleCollapse?: () => void;
  onNavigate?: () => void;
}

type Tone = 'orange' | 'amber' | 'violet' | 'sky' | 'emerald' | 'red' | 'slate';

interface SidebarItem {
  label: string;
  icon: LucideIcon;
  to: string;
  badge?: number;
  tone?: Tone;
  hasChildren?: boolean;
  workflow?: ReceptionWorkflowGroup;
}

interface SidebarSection {
  title: string;
  items: SidebarItem[];
}

interface SidebarVisibility {
  showSuper: boolean;
  showLanguageTools: boolean;
  showFinanceCommand: boolean;
}

const toneClasses: Record<Tone, { badge: string }> = {
  orange: { badge: 'bg-orange-100 text-orange-700' },
  amber: { badge: 'bg-amber-100 text-amber-700' },
  violet: { badge: 'bg-violet-100 text-violet-700' },
  sky: { badge: 'bg-sky-100 text-sky-700' },
  emerald: { badge: 'bg-emerald-100 text-emerald-700' },
  red: { badge: 'bg-red-100 text-red-700' },
  slate: { badge: 'bg-slate-100 text-slate-700' },
};

function getItemKey(sectionTitle: string, itemLabel: string) {
  return `${sectionTitle}:${itemLabel}`;
}

function visibleCount(value: unknown): number | undefined {
  return typeof value === 'number' && Number.isFinite(value) && value > 0 ? value : undefined;
}

function Badge({ children, tone = 'slate' }: { children: React.ReactNode; tone?: Tone }) {
  return (
    <span className={`rounded-full px-2 py-0.5 text-[11px] font-bold ${toneClasses[tone]?.badge || toneClasses.slate.badge}`}>
      {children}
    </span>
  );
}

function buildSidebarSections(
  counts?: Counts,
  pendingDanger?: number | null,
  visibility: SidebarVisibility = { showSuper: false, showLanguageTools: false, showFinanceCommand: false },
): SidebarSection[] {
  return [
    {
      title: 'Overview',
      items: [
        { label: 'Dashboard', icon: LayoutDashboard, to: '/reception' },
        { label: 'Tasks', icon: ListChecks, to: '/reception/tasks' },
      ],
    },
    {
      title: 'Students',
      items: [
        { label: 'All Students', icon: Users, to: '/reception/students', tone: 'violet' },
        { label: 'Old System', icon: FolderOpen, to: '/reception/old-system', tone: 'slate' },
        { label: 'Register Student', icon: UserPlus, to: '/reception/students/register' },
        { label: 'Waiting Students', icon: UserCheck, to: '/reception/students?category=waiting', badge: visibleCount(counts?.waiting), tone: 'amber', workflow: 'waiting' },
        { label: 'Requests & Interests', icon: Inbox, to: '/reception/requests', badge: visibleCount((counts?.requests ?? 0) + (counts?.course_interests ?? 0)), tone: 'sky', workflow: 'requests' },
      ],
    },
    {
      title: 'Classes',
      items: [
        { label: 'Summer Training', icon: GraduationCap, to: '/summer-training/operations', tone: 'emerald' },
        { label: 'Groups', icon: Users, to: '/reception/groups', badge: visibleCount(counts?.groups), tone: 'sky' },
        { label: 'Quarter', icon: CalendarDays, to: '/reception/quarter' },
        { label: 'Certificates', icon: Award, to: '/reception/certificates', tone: 'violet' },
        { label: 'Event Certificates', icon: Award, to: '/reception/event-certificates', tone: 'emerald' },
      ],
    },
    {
      title: 'Consultations',
      items: [
        { label: 'Consultations', icon: CalendarDays, to: '/reception/sales-consult-slots', badge: visibleCount(counts?.pending_sales_consult_queue), tone: 'sky', workflow: 'consultations' },
      ],
    },
    {
      title: 'Finance',
      items: [
        ...(visibility.showFinanceCommand ? [{ label: 'Finance', icon: DollarSign, to: '/reception/finance' } satisfies SidebarItem] : []),
        { label: 'Branch Finance', icon: WalletCards, to: '/reception/branch-finance' },
        { label: 'Sales Payment Requests', icon: Receipt, to: '/reception/sales-invoices', badge: visibleCount(counts?.pending_sales_invoices), tone: 'violet' },
        { label: 'Refunds', icon: RefreshCcw, to: '/reception/refunds', badge: visibleCount(counts?.pending_refunds), tone: 'red' },
      ],
    },
    ...(visibility.showLanguageTools
      ? [{
        title: 'Language Center',
        items: [
          { label: 'Language Center', icon: Languages, to: '/reception/language' },
          { label: 'Exams', icon: FileText, to: '/reception/exams' },
          { label: 'Create Exam', icon: GraduationCap, to: '/reception/exams/new' },
          { label: 'AI Import', icon: Bot, to: '/reception/language/exams/import' },
          { label: 'Materials Management', icon: FolderOpen, to: '/reception/materials' },
        ],
      } satisfies SidebarSection]
      : []),
    ...(visibility.showSuper
      ? [{
        title: 'Administration & Reports',
        items: [
            { label: 'Daily Reports', icon: FileText, to: '/reception/daily-reports' },
            { label: 'Group Reports', icon: FileText, to: '/reception/reports' },
            { label: 'Branch summary', icon: FileText, to: '/reception/reports/summary' },
            { label: 'Course Bookings Report', icon: FileText, to: '/reception/reports/bookings' },
            { label: 'Super Logs', icon: ShieldCheck, to: '/reception/super/logs', badge: visibleCount(pendingDanger ?? undefined), tone: 'red' },
            { label: 'Manage Tasks', icon: ListChecks, to: '/reception/tasks-manage' },
        ],
      } satisfies SidebarSection]
      : []),
  ];
}

function useActiveKey(sections: SidebarSection[]) {
  const location = useLocation();

  return useMemo(() => {
    const currentPath = location.pathname.replace(/^\/reception\/language\/materials(?=\/|$)/, '/reception/materials');
    const currentParams = new URLSearchParams(location.search);
    const workflow = getReceptionWorkflowGroup({ pathname: location.pathname, search: location.search });
    let bestMatch: { key: string; score: number } | null = null;

    for (const section of sections) {
      for (const item of section.items) {
        if (workflow && item.workflow === workflow) return getItemKey(section.title, item.label);
        const [itemPath, itemSearch] = item.to.split('?');
        const pathMatches =
          currentPath === itemPath ||
          (itemPath !== '/reception' && currentPath.startsWith(`${itemPath}/`));
        if (!pathMatches) continue;

        const itemParams = new URLSearchParams(itemSearch);
        let paramsMatch = true;
        itemParams.forEach((value, key) => {
          if (currentParams.get(key) !== value) paramsMatch = false;
        });
        if (!paramsMatch) continue;

        const score = itemPath.length + (itemSearch ? itemSearch.length + 1000 : 0);
        if (!bestMatch || score > bestMatch.score) {
          bestMatch = { key: getItemKey(section.title, item.label), score };
        }
      }
    }

    return bestMatch?.key ?? getItemKey('Overview', 'Dashboard');
  }, [location.pathname, location.search, sections]);
}

function SidebarContent({
  activeKey,
  sections,
  collapsed,
  onNavigate,
}: {
  activeKey: string;
  sections: SidebarSection[];
  collapsed?: boolean;
  onNavigate?: () => void;
}) {
  const { t: receptionText } = useReceptionLocale();

  const [openSections, setOpenSections] = useState<Record<string, boolean>>(() => {
    try {
      const saved = localStorage.getItem(SECTION_STATE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved) as Record<string, boolean>;
        return sections.reduce((acc, section) => ({ ...acc, [section.title]: parsed[section.title] ?? true }), {});
      }
    } catch {
      /* ignore */
    }
    return sections.reduce((acc, section) => ({ ...acc, [section.title]: true }), {});
  });

  useEffect(() => {
    try {
      localStorage.setItem(SECTION_STATE_KEY, JSON.stringify(openSections));
    } catch {
      /* ignore */
    }
  }, [openSections]);

  const toggleSection = (sectionTitle: string) => {
    setOpenSections((current) => ({ ...current, [sectionTitle]: !current[sectionTitle] }));
  };

  return (
      <div className={`py-4 ${collapsed ? 'px-2' : 'px-3'}`}>
        <nav className={collapsed ? 'space-y-4 pb-2' : 'space-y-5 pb-2'}>
          {sections.map((section) => {
            const isOpen = openSections[section.title] ?? true;

            if (collapsed) {
              return (
                <section key={section.title} aria-label={receptionText(section.title)}>
                  <div className="mx-auto mb-2 h-px w-10 rounded-full bg-slate-200" title={receptionText(section.title)} />
                  <div className="space-y-1">
                    {section.items.map((item) => {
                      const Icon = item.icon;
                      const itemKey = getItemKey(section.title, item.label);
                      const active = activeKey === itemKey;
                      return (
                        <PrefetchLink
                          key={itemKey}
                          to={item.to}
                          title={receptionText(item.label)}
                          aria-label={receptionText(item.label)}
                          aria-current={active ? 'page' : undefined}
                          onClick={onNavigate}
                          className={`relative grid h-11 w-full place-items-center rounded-2xl no-underline transition ${
                            active
                              ? 'bg-violet-50 text-violet-700 ring-1 ring-violet-100'
                              : 'text-slate-500 hover:bg-slate-50 hover:text-slate-950'
                          }`}
                        >
                          <Icon size={19} />
                          {item.badge ? (
                            <span className={`absolute right-1 top-1 rounded-full px-1.5 py-0.5 text-[9px] font-black leading-none ${toneClasses[item.tone ?? 'slate']?.badge || toneClasses.slate.badge}`}>
                              {item.badge}
                            </span>
                          ) : null}
                        </PrefetchLink>
                      );
                    })}
                  </div>
                </section>
              );
            }

            return (
              <section key={section.title}>
                <button
                  type="button"
                  onClick={() => toggleSection(section.title)}
                  className="mb-2 flex w-full items-center justify-between rounded-xl px-2 py-1 text-xs font-bold uppercase tracking-[0.08em] text-slate-400 hover:bg-slate-50 hover:text-slate-600"
                >
                  <span>{receptionText(section.title)}</span>
                  <ChevronDown size={14} className={`transition ${isOpen ? 'rotate-0' : '-rotate-90'}`} />
                </button>

                {isOpen ? (
                  <div className="space-y-1">
                    {section.items.map((item) => {
                      const Icon = item.icon;
                      const itemKey = getItemKey(section.title, item.label);
                      const active = activeKey === itemKey;
                      return (
                        <PrefetchLink
                          key={itemKey}
                          to={item.to}
                          aria-current={active ? 'page' : undefined}
                          onClick={onNavigate}
                          className={`group flex w-full items-center gap-3 rounded-2xl px-3 py-2.5 text-left text-sm font-semibold no-underline transition ${
                            active
                              ? 'bg-violet-50 text-violet-700 ring-1 ring-violet-100'
                              : 'text-slate-700 hover:bg-slate-50 hover:text-slate-950'
                          }`}
                        >
                          <Icon size={18} className={active ? 'text-violet-700' : 'text-slate-500 group-hover:text-slate-800'} />
                          <span className="min-w-0 flex-1 truncate">{receptionText(item.label)}</span>
                          {item.badge ? <Badge tone={item.tone}>{item.badge}</Badge> : null}
                          {item.hasChildren ? <ChevronRight size={15} className="text-slate-400" /> : null}
                        </PrefetchLink>
                      );
                    })}
                  </div>
                ) : null}
              </section>
            );
          })}
        </nav>
      </div>

  );
}

export const ReceptionSidebar: React.FC<ReceptionSidebarProps> = ({
  counts,
  collapsed,
  onToggleCollapse,
  onNavigate,
}) => {
  const { t: operationsText } = useOperationsLocale();

  const { showSuper, pendingDanger, isManager } = useUserProfile();
  const { user } = useAuth();
  const location = useLocation();
  const role = String(user?.role ?? '').trim().toLowerCase();
  const showLanguageTools = role === 'reception' || role === 'super_reception' || role === 'general_manager' || hasReceptionAllBranchesGrant(user);
  const { effectiveCounts } = useDashboardCounts({ counts });
  const sections = useMemo(() => {
    const allSections = buildSidebarSections(effectiveCounts, pendingDanger, {
      showSuper,
      showLanguageTools,
      showFinanceCommand: showSuper || isManager,
    });
    const isLanguagePage = /^\/reception\/(?:language(?:\/|$)|exams(?:\/|$)|attempts(?:\/|$)|materials(?:\/|$))/.test(location.pathname);
    if (!showLanguageTools || !isLanguagePage) return allSections;
    return [
      allSections.find(section => section.title === 'Language Center')!,
      { title: 'Reception', items: [{ label: 'Back to Reception', icon: ArrowLeft, to: '/reception' }] },
    ];
  }, [effectiveCounts, isManager, location.pathname, pendingDanger, showLanguageTools, showSuper]);
  const activeKey = useActiveKey(sections);
  return <CrmNavigationSidebar label={operationsText("Reception navigation")} collapsed={collapsed} onToggle={onToggleCollapse}>
    {compact => <SidebarContent activeKey={activeKey} sections={sections} collapsed={compact} onNavigate={onNavigate} />}
  </CrmNavigationSidebar>;
};
